import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { formatPln } from '../catalog/money';
import { formatDayText } from '../events/fields';
import { PlanEditor } from './PlanEditor';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { formText, METHODS, PAYMENTS_KEY, RECEIVABLES_RESOURCE, stateLabel, type EventPayments, type Receivable } from './types';
import type { Settlement } from '../events/labels';
import './payments.css';

const money = (g: number | undefined) => (g === undefined ? '—' : formatPln(g));
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

/**
 * "Płatności" on the event card (F36, spec E3 §7): the payment plan (default 30% + rest, or edited), the event's
 * receivables (installments, then invoices) with "Odnotuj wpłatę", the payments booked and the sums. Amounts only
 * for whoever sees them; actions only with the rights.
 */
/** `settlement`: the event's „Rozliczenie” (absent without the field); „bez faktury” gives added installments its form. */
export function EventPaymentsPanel({ eventId, me, settlement }: { eventId: string; me: Me; settlement?: Settlement | undefined }) {
  const queryClient = useQueryClient();
  const key = [...PAYMENTS_KEY, 'event', eventId] as const;
  const data = useQuery({ queryKey: key, queryFn: () => api<EventPayments>(`/api/payments/events/${eventId}`) });
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState<Receivable | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
  const makeDefault = useMutation({ mutationFn: () => api(`/api/payments/events/${eventId}/plan/default`, { method: 'POST' }), onSuccess: refresh });
  const [removeError, setRemoveError] = useState('');
  async function removePayment(id: string) {
    try {
      await api(`/api/payments/allocations/${id}?reason=${encodeURIComponent('usunięta na karcie wydarzenia')}`, { method: 'DELETE' });
      setRemoveError('');
    } catch (e) {
      setRemoveError(errorText(e, 'Nie udało się usunąć wpłaty'));
    }
    refresh();
  }
  const navigate = useNavigate();
  // F40: advance invoices of the event by their installment ("Wystaw fakturę zaliczkową" at a paid installment)
  const mayInvoice = can(me, 'invoices.invoices', 'create');
  const invoices = useQuery({
    queryKey: ['invoices', 'event', eventId],
    queryFn: () => api<{ invoices: Array<{ id: string; kind: string; state: string; number: string | null; installmentId: string | null }> }>(`/api/invoices/events/${eventId}`),
    enabled: can(me, 'invoices.invoices', 'view'),
  });
  const advanceOf = new Map((invoices.data?.invoices ?? []).filter((i) => i.kind === 'ZAL' && i.state !== 'anulowana' && i.installmentId).map((i) => [i.installmentId!, i]));
  const advance = useMutation({
    mutationFn: (installmentId: string) => api<{ id: string }>(`/api/invoices/events/${eventId}/advance`, { method: 'POST', body: { installmentId } }),
    onSuccess: (inv) => navigate(`/finanse/faktury/${inv.id}`),
  });
  const d = data.data;
  const plannable = can(me, RECEIVABLES_RESOURCE, 'edit') && fieldVisible(me, RECEIVABLES_RESOURCE, 'amountGrosze');
  const recording = can(me, RECEIVABLES_RESOURCE, 'create') && fieldVisible(me, RECEIVABLES_RESOURCE, 'amountGrosze');
  const invoiced = d?.receivables.some((r) => r.source === 'invoice' && r.state !== 'anulowana') ?? false;
  const live = d?.receivables.filter((r) => r.state !== 'anulowana') ?? [];
  const cancelled = d?.receivables.filter((r) => r.state === 'anulowana') ?? [];
  const labelOf = new Map((d?.receivables ?? []).map((r) => [r.id, r.label]));
  return (
    <div role="tabpanel" aria-label="Płatności" className="pay-tab">
      {data.error && <p className="alert" role="alert">{errorText(data.error, 'Nie udało się wczytać płatności')}</p>}
      {d && (
        <>
          {d.paidGrosze !== undefined && (
            <dl className="pay-sums">
              <div>
                <dt>Wpłacono</dt>
                <dd>{money(d.paidGrosze)}</dd>
              </div>
              <div>
                <dt>Do zapłaty</dt>
                <dd>{money(d.openGrosze)}</dd>
              </div>
              {(d.overdueGrosze ?? 0) > 0 && (
                <div className="pay-overdue">
                  <dt>Zaległe</dt>
                  <dd>{money(d.overdueGrosze)}</dd>
                </div>
              )}
              {(d.contractorCredit ?? 0) > 0 && (
                <div>
                  <dt>Nadpłata kontrahenta</dt>
                  <dd>{money(d.contractorCredit)}</dd>
                </div>
              )}
            </dl>
          )}

          <section className="fin-section" aria-label="Plan płatności" data-feature-id="payments.plan">
            <div className="pay-section-head">
              <h3>Plan płatności</h3>
              {plannable && !invoiced && !editing && (d.plan ? (
                <button className="btn" type="button" onClick={() => setEditing(true)}>
                  Zmień plan
                </button>
              ) : (
                <button className="btn primary" type="button" disabled={makeDefault.isPending} onClick={() => makeDefault.mutate()}>
                  Plan domyślny
                </button>
              ))}
            </div>
            {makeDefault.error && <p className="alert" role="alert">{errorText(makeDefault.error, 'Nie udało się utworzyć planu')}</p>}
            {!d.plan && <p className="muted">{invoiced ? 'Należność powstała z faktury.' : 'Bez planu: należność powstanie z faktury. Plan domyślny to 30% zaliczki za tydzień i reszta w ostatnim dniu wydarzenia.'}</p>}
            {d.plan && invoiced && <p className="muted">Faktura zastąpiła raty planu z fakturą — wpłaty na nie przeszły na należność z faktury. Raty bez faktury zostają do zapłaty.</p>}
            {d.plan && editing && (
              <PlanEditor
                newForm={settlement && settlement !== 'faktura' ? settlement : 'faktura'}
                eventId={eventId}
                plan={d.plan}
                onClose={() => setEditing(false)}
                onSaved={() => {
                  setEditing(false);
                  refresh();
                }}
              />
            )}
          </section>

          <section className="fin-section" aria-label="Należności">
            <h3>Należności</h3>
            {live.length === 0 && <p className="muted">Brak należności.</p>}
            {live.length > 0 && (
              <div className="table-wrap">
                <table className="table pay-table" aria-label="Należności wydarzenia">
                  <thead>
                    <tr>
                      <th scope="col">Należność</th>
                      <th scope="col">Termin</th>
                      <th scope="col" className="num">
                        Kwota
                      </th>
                      <th scope="col" className="num">
                        Pozostało
                      </th>
                      <th scope="col">Stan</th>
                      <th scope="col">
                        <span className="visually-hidden">Działania</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {live.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {r.label}
                          {r.source === 'manual' && <span className="pay-tag">ręczna</span>}
                          <span className="pay-sub">{formText(r) || METHODS[r.method]}</span>
                        </td>
                        <td className="nowrap">{formatDayText(r.dueDate)}</td>
                        <td className="num nowrap">{money(r.amountGrosze)}</td>
                        <td className="num nowrap">{(r.refundGrosze ?? 0) > 0 ? <span className="pay-refund">Do zwrotu {money(r.refundGrosze)}</span> : money(r.remainingGrosze)}</td>
                        <td>
                          <span className="pay-state" data-state={r.overdueDays > 0 ? 'zalegla' : r.state}>
                            {stateLabel(r)}
                          </span>
                        </td>
                        <td className="q-actions">
                          {recording && r.state === 'otwarta' && (
                            <button className="btn ghost small" type="button" onClick={() => setPaying(r)}>
                              Odnotuj wpłatę
                            </button>
                          )}
                          {r.source === 'installment' && r.state === 'zaplacona' && advanceOf.has(r.sourceId) && (
                            <a className="btn ghost small" href={`/finanse/faktury/${advanceOf.get(r.sourceId)!.id}`} onClick={(e) => (e.preventDefault(), navigate(`/finanse/faktury/${advanceOf.get(r.sourceId)!.id}`))}>
                              {advanceOf.get(r.sourceId)!.number ?? 'Faktura zaliczkowa (robocza)'}
                            </a>
                          )}
                          {mayInvoice && invoices.data && r.source === 'installment' && r.state === 'zaplacona' && !advanceOf.has(r.sourceId) && (
                            <button className="btn small" type="button" disabled={advance.isPending} onClick={() => advance.mutate(r.sourceId)} data-feature-id="invoices.advance">
                              Wystaw fakturę zaliczkową
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {advance.error && <p className="alert" role="alert">{errorText(advance.error, 'Nie udało się utworzyć faktury zaliczkowej')}</p>}
            {cancelled.length > 0 && <p className="muted small">Anulowane: {cancelled.map((r) => ((r.refundGrosze ?? 0) > 0 ? `${r.label} (do zwrotu ${money(r.refundGrosze)})` : r.label)).join(', ')}.</p>}
          </section>

          {d.allocations.length > 0 && (
            <section className="fin-section" aria-label="Wpłaty wydarzenia" data-feature-id="payments.record">
              <h3>Wpłaty</h3>
              {removeError && <p className="alert" role="alert">{removeError}</p>}
              <div className="table-wrap">
                <table className="table pay-table" aria-label="Wpłaty">
                  <thead>
                    <tr>
                      <th scope="col">Data</th>
                      <th scope="col">Na</th>
                      <th scope="col">Sposób</th>
                      <th scope="col" className="num">
                        Kwota
                      </th>
                      <th scope="col" className="wide-only">
                        Uwagi
                      </th>
                      <th scope="col">
                        <span className="visually-hidden">Działania</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.allocations.map((a) => (
                      <tr key={a.id}>
                        <td className="nowrap">{formatDayText(a.paidOn)}</td>
                        <td>{(a.receivableId && labelOf.get(a.receivableId)) || ''}</td>
                        <td>
                          {a.refund ? 'Zwrot' : a.method === 'kompensata' ? 'Kompensata' : METHODS[a.method]}
                          {a.transactionId && <span className="pay-sub">{a.confidence === 'auto' ? 'z wyciągu, automatycznie' : 'z wyciągu'}</span>}
                        </td>
                        <td className="num nowrap">{a.refund && a.amountGrosze !== undefined ? money(-a.amountGrosze) : money(a.amountGrosze)}</td>
                        <td className="wide-only">{a.note}</td>
                        <td className="q-actions">
                          {recording && (
                            <button className="btn icon ghost" type="button" aria-label={`Usuń wpłatę z ${formatDayText(a.paidOn)}`} title="Usuń wpłatę" onClick={() => void removePayment(a.id)}>
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
      {paying && (
        <RecordPaymentDialog
          receivable={paying}
          onClose={() => setPaying(null)}
          onSaved={() => {
            setPaying(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
